import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import DiscountRoundedIcon from '@mui/icons-material/DiscountRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '../ErrorBoundary';
import { LanguageSwitcher } from '../LanguageSwitcher';

import { env } from '@/shared/config/env';
import { accountConsoleUrl } from '@/shared/auth/accountConsole';
import { PasskeyEnrolmentReturn } from '@/shared/auth/PasskeyEnrolmentReturn';
import { PasskeyNudgeBanner } from '@/shared/auth/PasskeyNudgeBanner';
import { StepUpReplay } from '@/shared/auth/StepUpReplay';
import { useAuth } from '@/shared/auth/useAuth';
import { GlobalFeedbackSnackbar } from '@/shared/feedback';
import { appSection } from '@/shared/navigation/appSections';
import type { AppSectionKey } from '@/shared/navigation/appSections';
import { useSectionAccess } from '@/shared/navigation/useSectionAccess';
import { useTenant } from '@/shared/tenant/useTenant';
import { useThemeMode } from '@/shared/theme/useThemeMode';

const DRAWER_WIDTH = 268;
/** Collapsed rail: one 44px target plus the 16px gutters the design system's 4px grid gives it. */
const RAIL_WIDTH = 76;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'obzr.merchantPortal.sidebarCollapsed';

/**
 * Whether the desktop sidebar is collapsed to an icon rail, remembered across sessions.
 *
 * Persisted per browser rather than per user: it is a viewport preference, not account data — someone
 * on a narrow laptop wants the rail everywhere, and it must not wait for a round-trip before the shell
 * can paint. Reads are guarded because storage throws outright in some privacy modes, and a shell that
 * cannot render is a worse failure than a forgotten preference.
 */
function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
      /* Ignored: the preference is a convenience, never a precondition for rendering. */
    }
  }, [collapsed]);

  return [collapsed, useCallback(() => setCollapsed((value) => !value), [])];
}

/**
 * A sidebar entry: an icon, and the registry key of the section it opens.
 *
 * <p>Label, destination, required permission and feature flag all come from `APP_SECTIONS`, so the
 * menu cannot advertise a different permission from the one the route enforces. Only the icon lives
 * here, because it is the one property that is genuinely about the sidebar and nothing else.
 */
interface NavItem {
  sectionKey: AppSectionKey;
  icon: ReactNode;
}

/** A nav entry with its registry values resolved, ready to render. */
interface ResolvedNavItem {
  /** The registry key — a stable React key, unlike the label (two entries may share one). */
  sectionKey: AppSectionKey;
  labelKey: string;
  to: string;
  icon: ReactNode;
}

interface NavSection {
  /** Optional `common.json` translation key for the group heading shown above the items. */
  headingKey?: string;
  /**
   * The section's own icon — the ONLY thing rendered for it while the sidebar is collapsed, where
   * hovering it opens a flyout listing the items below. A section without a heading (Dashboard) is a
   * single destination, so its icon is its item's and the button navigates instead of opening a menu.
   */
  icon?: ReactNode;
  items: NavItem[];
}

// Grouped, permission-aware navigation. Operational sections (Products, Orders,
// Customers, Media) are top-level workspaces; each one's domain configuration
// lives behind a settings gear on its page. Cross-cutting, store-level
// configuration lives under the grouped Settings area.
//
// Labels are i18n KEYS (resolved via t() in DrawerContent), not literal strings — this array
// lives outside the component so it isn't rebuilt on every render, but every reader wanting the
// visible text must go through the translation function.
const NAV_SECTIONS: NavSection[] = [
  {
    items: [{ sectionKey: 'dashboard', icon: <SpaceDashboardRoundedIcon /> }],
  },
  {
    headingKey: 'nav.sections.catalog',
    icon: <Inventory2RoundedIcon />,
    items: [
      { sectionKey: 'catalogProducts', icon: <Inventory2RoundedIcon /> },
      { sectionKey: 'catalogCategories', icon: <CategoryRoundedIcon /> },
      // One entry for the whole catalog data model. Behind it, `CatalogStructureLayout` tabs between
      // templates, attribute groups, attributes and units of measure — four dictionaries that describe
      // one model and were previously four separate sidebar lines (units of measure sat under
      // Logistics, further away still). Products and Categories are daily work and keep their own
      // entries; the model is configured rarely, so it earns one.
      { sectionKey: 'catalogStructure', icon: <AccountTreeRoundedIcon /> },
    ],
  },
  {
    headingKey: 'nav.sections.commerce',
    icon: <SellRoundedIcon />,
    items: [
      { sectionKey: 'catalogPricing', icon: <SellRoundedIcon /> },
      { sectionKey: 'promo', icon: <DiscountRoundedIcon /> },
    ],
  },
  {
    headingKey: 'nav.sections.salesChannels',
    icon: <StorefrontRoundedIcon />,
    items: [
      { sectionKey: 'catalogPublication', icon: <HubRoundedIcon /> },
      { sectionKey: 'content', icon: <ArticleRoundedIcon /> },
      { sectionKey: 'channelStock', icon: <CallSplitRoundedIcon /> },
    ],
  },
  {
    headingKey: 'nav.sections.customers',
    icon: <GroupsRoundedIcon />,
    items: [
      { sectionKey: 'customers', icon: <GroupsRoundedIcon /> },
      { sectionKey: 'conversations', icon: <ForumRoundedIcon /> },
      { sectionKey: 'loyalty', icon: <CardGiftcardRoundedIcon /> },
      { sectionKey: 'messaging', icon: <CampaignRoundedIcon /> },
      { sectionKey: 'chatbot', icon: <SmartToyRoundedIcon /> },
      { sectionKey: 'promoCalendar', icon: <WorkspacePremiumRoundedIcon /> },
    ],
  },
  {
    headingKey: 'nav.sections.logistics',
    icon: <WarehouseRoundedIcon />,
    items: [
      { sectionKey: 'inventory', icon: <WarehouseRoundedIcon /> },
      { sectionKey: 'storeLocations', icon: <StoreRoundedIcon /> },
    ],
  },
  {
    headingKey: 'nav.sections.administration',
    icon: <SettingsRoundedIcon />,
    items: [
      // Called "Users": the section is no longer just a staff list — it is the workspace where users,
      // groups and per-domain access are administered together. It was briefly labelled "Company",
      // which collided with Settings → Company (the tenant's own legal/registration details) — two
      // unrelated destinations wearing one word. The URL stays `/team` so links keep working.
      { sectionKey: 'team', icon: <ManageAccountsRoundedIcon /> },
      { sectionKey: 'billing', icon: <CreditCardRoundedIcon /> },
      { sectionKey: 'audit', icon: <ReceiptLongRoundedIcon /> },
      // Opens when the user can view at least one settings domain — it used to be the one entry
      // shown unconditionally, which handed everybody a hub of locked cards.
      { sectionKey: 'settings', icon: <SettingsRoundedIcon /> },
    ],
  },
];

function isNavItemActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Splits the configured application name into its name and its environment tag.
 *
 * The contour is baked into `VITE_APP_NAME` at build time — `'Merchant Portal (stage)'` on stage,
 * a bare `'Merchant Portal'` on production (see .gitlab-ci.yml). The sidebar renders the two parts on
 * separate lines, the tag as a chip, so an operator can tell at a glance which contour they are
 * changing data on. Production deliberately yields no tag: a badge that is always present stops being
 * read, and the absence of one is itself the signal that this is the real thing.
 */
export function parseAppName(appName: string): { name: string; envTag: string | null } {
  const match = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(appName.trim());
  if (!match) {
    return { name: appName.trim(), envTag: null };
  }
  const [, name, envTag] = match;
  // A trailing "()" or a name that is nothing BUT a tag would leave the sidebar unlabelled.
  return name.trim()
    ? { name: name.trim(), envTag: envTag.trim() }
    : { name: appName.trim(), envTag: null };
}

function initialsFromName(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function calculateTenantSelectorWidth(
  tenantName: string | null,
  availableTenants: Array<{ name: string }>,
  placeholderLabel = 'Select tenant',
): number {
  const candidates = [
    tenantName ?? placeholderLabel,
    ...availableTenants.map((tenant) => tenant.name),
  ];
  const widestTenant = candidates.reduce((widest, candidate) => {
    const characterCount = Array.from(candidate).length;
    const width = characterCount * 8.2 + candidate.split(' ').length * 2;
    return width > widest ? width : widest;
  }, 0);

  // Leading icon + text block gap + trailing chevron + horizontal padding.
  return Math.ceil(widestTenant + 88);
}

function BrandMark() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 38,
        height: 38,
        flexShrink: 0,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        background: (t) =>
          `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
        boxShadow: '0 6px 16px rgba(18, 124, 113, 0.32)',
      }}
    >
      <StorefrontRoundedIcon sx={{ fontSize: 21 }} />
    </Box>
  );
}

/** Tenant selector placeholder. Switches the active (mock) tenant. */
function TenantSelector() {
  const { t } = useTranslation();
  const { tenantName, tenantId, availableTenants, setTenant } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const placeholderLabel = t('nav.tenantSelector.placeholder');
  const selectorWidth = useMemo(
    () => calculateTenantSelectorWidth(tenantName, availableTenants, placeholderLabel),
    [availableTenants, tenantName, placeholderLabel],
  );

  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<StorefrontRoundedIcon />}
        endIcon={<ExpandMoreRoundedIcon />}
        sx={{
          width: selectorWidth,
          flexShrink: 0,
          justifyContent: 'flex-start',
          textAlign: 'left',
          px: 1.5,
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { flexShrink: 0 },
          '& .MuiButton-startIcon': { mr: 1 },
          '& .MuiButton-endIcon': { ml: 0.75 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1 }}
          >
            {t('nav.tenantSelector.label')}
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {tenantName ?? placeholderLabel}
          </Typography>
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          sx: { textAlign: 'left' },
        }}
        PaperProps={{
          sx: {
            minWidth: selectorWidth,
          },
        }}
      >
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
          {t('nav.tenantSelector.switchTenant')}
        </ListSubheader>
        {availableTenants.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === tenantId}
            onClick={() => {
              setTenant(option);
              setAnchorEl(null);
            }}
            sx={{ justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'nowrap' }}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Light/dark color-mode toggle (OBZR design system). */
function ThemeModeToggle() {
  const { t } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Tooltip title={isDark ? t('nav.themeToggle.toLight') : t('nav.themeToggle.toDark')}>
      <IconButton color="inherit" aria-label="Toggle color mode" onClick={toggleMode}>
        {isDark ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
      </IconButton>
    </Tooltip>
  );
}

/**
 * User account menu: who's signed in, a single "Profile settings" entry to the Keycloak Account
 * Console (own profile/password/2FA — opens in a new tab), and Sign out. Tenant/company switching
 * lives separately in {@link TenantSelector}, not here.
 */
function UserMenu() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const initials = user ? initialsFromName(user.name) : '?';
  const accountConsoleHref = accountConsoleUrl();

  return (
    <>
      <Tooltip title={t('nav.userMenu.account')}>
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          aria-label={t('nav.userMenu.openMenu')}
        >
          <Avatar
            sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {user?.name ?? t('nav.userMenu.guest')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email ?? t('nav.userMenu.notSignedIn')}
          </Typography>
        </Box>
        <Divider />
        {/*
          First, above "Profile settings": the one destination every signed-in employee needs
          regardless of permissions, including someone whose access has not been set up yet
          (adr-merchant-portal-020). Unlike every other item here it is an in-app route, not a
          console deep link — RouterLink, not a plain href.
        */}
        <MenuItem component={RouterLink} to="/account/security" onClick={() => setAnchorEl(null)}>
          <ListItemIcon>
            <FingerprintRoundedIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.userMenu.signInSecurity')}
        </MenuItem>
        <MenuItem
          component="a"
          href={accountConsoleHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setAnchorEl(null)}
        >
          <ListItemIcon>
            <AccountCircleRoundedIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.userMenu.profileSettings')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            logout();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.userMenu.signOut')}
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * The brand block at the very top of the sidebar.
 *
 * It is deliberately NOT a bordered card. Previously a `Toolbar` closed by a `Divider`, boxed in on the
 * right by the drawer's own border, it read as a separate tile pinned into the corner rather than the
 * head of the navigation column. Dropping the rule underneath lets the sidebar read as one continuous
 * surface from the mark down to the last nav item — the only line left is the vertical one that
 * separates navigation from content, which is the distinction that actually means something.
 */
/**
 * Colour for the contour badge, mirroring the Keycloak themes' environment indicator so the two
 * products agree: amber warns that stage looks like production but is not, blue marks dev as
 * throwaway. An unrecognised contour still gets a neutral badge rather than being hidden — an
 * unlabelled deployment is the one case where silence is dangerous.
 */
function envTagColor(envTag: string): 'warning' | 'info' | 'secondary' {
  const tag = envTag.toLowerCase();
  if (tag.startsWith('stage') || tag.startsWith('preprod')) return 'warning';
  if (tag.startsWith('dev') || tag.startsWith('local') || tag.startsWith('test')) return 'info';
  return 'secondary';
}

export function BrandBlock({
  collapsed,
  appName = env.appName,
}: {
  collapsed: boolean;
  /** Overridable so the block can be rendered without reaching into build-time config. */
  appName?: string;
}) {
  const { name, envTag } = useMemo(() => parseAppName(appName), [appName]);

  return (
    <Box
      sx={{
        minHeight: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: collapsed ? 0 : 2.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      <BrandMark />
      {!collapsed ? (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap lineHeight={1.15}>
            {name}
          </Typography>
          {envTag ? (
            <Chip
              label={envTag}
              size="small"
              // Reads as the contour badge on the Keycloak screens the merchant is bounced through
              // for login and profile: a filled uppercase pill, colour-coded the same way — amber for
              // stage, blue for dev. Same signal in both products, so "which contour am I on" is
              // answered identically wherever the user happens to be looking.
              color={envTagColor(envTag)}
              sx={{
                mt: 0.25,
                height: 18,
                borderRadius: 999,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                '& .MuiChip-label': { px: 1, fontSize: 10, fontWeight: 700 },
              }}
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

interface ResolvedSection {
  headingKey?: string;
  icon?: ReactNode;
  items: ResolvedNavItem[];
}

/**
 * One row of the collapsed rail: the section's icon, with its items in a flyout.
 *
 * Opens on hover AND on keyboard focus — a rail that only answers the mouse is unusable to anyone
 * navigating by keyboard, and the items inside are the only way to reach those pages while collapsed.
 * Closing is delayed so the pointer can cross the gap between the icon and the panel without the panel
 * vanishing underneath it, which is the failure that makes hover menus feel broken.
 */
function RailSection({ section, pathname }: { section: ResolvedSection; pathname: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  }, [cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);

  const sectionActive = section.items.some((item) => isNavItemActive(pathname, item.to));
  // A section with no heading is a single destination (Dashboard): the rail button IS the link, so
  // there is nothing to put in a menu beyond the label itself.
  const isSingleDestination = !section.headingKey && section.items.length === 1;
  const label = section.headingKey ? t(section.headingKey) : t(section.items[0].labelKey);

  return (
    <Box
      ref={anchorRef}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={(event) => {
        if (!anchorRef.current?.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
      sx={{ px: 1.5, mb: 0.5 }}
    >
      <ListItemButton
        {...(isSingleDestination
          ? { component: RouterLink, to: section.items[0].to }
          : { 'aria-haspopup': 'menu' as const, 'aria-expanded': open })}
        selected={sectionActive}
        aria-label={label}
        sx={{ justifyContent: 'center', px: 0, minHeight: 44, borderRadius: 2 }}
      >
        <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
          {section.icon ?? section.items[0].icon}
        </ListItemIcon>
      </ListItemButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="right-start"
        transition
        // Above the app bar, or the topmost flyout would slide underneath it.
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }} timeout={150}>
            <Paper
              variant="outlined"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              sx={{ minWidth: 208, py: 0.5, boxShadow: 3, overflow: 'hidden' }}
            >
              {isSingleDestination ? (
                <Typography variant="body2" fontWeight={600} sx={{ px: 2, py: 1 }}>
                  {label}
                </Typography>
              ) : (
                <List dense disablePadding role="menu" aria-label={label} sx={{ px: 0.5 }}>
                  <ListSubheader
                    component="div"
                    sx={{ bgcolor: 'transparent', lineHeight: '32px' }}
                  >
                    {label}
                  </ListSubheader>
                  {section.items.map((item) => (
                    <ListItem key={item.sectionKey} disablePadding role="none">
                      <ListItemButton
                        component={RouterLink}
                        to={item.to}
                        role="menuitem"
                        selected={isNavItemActive(pathname, item.to)}
                        onClick={() => setOpen(false)}
                        sx={{ borderRadius: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={t(item.labelKey)} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

/** Drawer body: brand, permission-filtered navigation, and the collapse control. */
function DrawerContent({
  collapsed = false,
  onToggleCollapse,
}: {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { canEnter } = useSectionAccess();

  // Both the visibility test and the destination come from APP_SECTIONS — the same registry the route
  // guards read — so an entry can never link somewhere the router will refuse, and a section the
  // router opens can never be missing from the menu. A heading whose every item is filtered out
  // disappears with them, rather than leaving an empty group.
  const sections: ResolvedSection[] = NAV_SECTIONS.map((section) => ({
    headingKey: section.headingKey,
    icon: section.icon,
    items: section.items
      .filter((item) => canEnter(item.sectionKey))
      .map((item) => {
        const { labelKey, path } = appSection(item.sectionKey);
        return { sectionKey: item.sectionKey, labelKey, to: path, icon: item.icon };
      }),
  })).filter((section) => section.items.length > 0);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BrandBlock collapsed={collapsed} />

      <Box
        component="nav"
        aria-label={t('nav.primaryNavigation')}
        sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}
      >
        {collapsed
          ? sections.map((section, index) => (
              <RailSection
                key={section.headingKey ?? `section-${index}`}
                section={section}
                pathname={location.pathname}
              />
            ))
          : sections.map((section, index) => (
              <List
                key={section.headingKey ?? `section-${index}`}
                subheader={
                  section.headingKey ? (
                    <ListSubheader component="div" sx={{ bgcolor: 'transparent' }}>
                      {t(section.headingKey)}
                    </ListSubheader>
                  ) : undefined
                }
                sx={{ px: 1.5 }}
              >
                {section.items.map((item) => (
                  <ListItem key={item.sectionKey} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      component={RouterLink}
                      to={item.to}
                      selected={isNavItemActive(location.pathname, item.to)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={t(item.labelKey)} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ))}
      </Box>

      {onToggleCollapse ? (
        <>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end' }}>
            <Tooltip title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}>
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
                aria-expanded={!collapsed}
              >
                {collapsed ? (
                  <KeyboardDoubleArrowRightRoundedIcon fontSize="small" />
                ) : (
                  <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

/**
 * Application layout shell.
 *
 * Provides the top app bar (brand, tenant selector, notifications, user menu),
 * a responsive side navigation (permanent on desktop, temporary on mobile),
 * and the main content area where routed pages render via `<Outlet />`.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const location = useLocation();
  // The rail only exists on desktop; the mobile drawer is an overlay that is either open or gone, so
  // collapsing it would leave a rail with no way back.
  const sidebarWidth = collapsed ? RAIL_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${sidebarWidth}px)` },
          ml: { md: `${sidebarWidth}px` },
          // Matches the drawer, so the two move as one surface rather than the bar sliding over a
          // sidebar that is still resizing underneath it.
          transition: (theme) =>
            theme.transitions.create(['width', 'margin-left'], {
              duration: theme.transitions.duration.shorter,
            }),
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t('nav.openNavigation')}
              onClick={() => setMobileOpen(true)}
            >
              <MenuRoundedIcon />
            </IconButton>
          ) : null}

          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            <BrandMark />
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {env.appName}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TenantSelector />
          </Box>

          <LanguageSwitcher />

          <ThemeModeToggle />

          <Tooltip title={t('nav.notifications')}>
            <span>
              <IconButton color="inherit" aria-label={t('nav.notifications')} disabled>
                <NotificationsNoneRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <UserMenu />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: sidebarWidth }, flexShrink: { md: 0 } }}
        aria-label={t('nav.mainNavigation')}
      >
        {/* Mobile: temporary overlay drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          <DrawerContent />
        </Drawer>

        {/* Desktop: permanent docked drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarWidth,
              overflowX: 'hidden',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                }),
            },
          }}
        >
          <DrawerContent collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${sidebarWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/*
            Above the routed content, inside the same scroll column, so it scrolls away with the page
            rather than covering it — never blocking (adr-merchant-portal-020 D5). Renders nothing for
            almost every session: see `PasskeyNudgeBanner`'s own doc comment for its trigger conditions.
          */}
          <PasskeyNudgeBanner />
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>

      <GlobalFeedbackSnackbar />
      {/*
        Order matters: `StepUpReplay` publishes onto the global feedback bus from its own effect, and that
        bus holds exactly ONE listener, registered by `GlobalFeedbackSnackbar`'s effect. Effects run in
        tree order, so mounting the replay second is what guarantees there is something listening when it
        speaks. (plan-auth-001 Phase 4.4)
      */}
      <StepUpReplay />
      {/*
        `PasskeyEnrolmentReturn` publishes onto the same bus for the same reason and so obeys the same
        "after GlobalFeedbackSnackbar" rule; after StepUpReplay too, purely so the two round-trip
        reporters sit together (user-story-merchant-portal-060, adr-merchant-portal-020 D4).
      */}
      <PasskeyEnrolmentReturn />
    </Box>
  );
}
